import { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Canvas } from '@react-three/fiber';
import { PerformanceMonitor } from '@react-three/drei';
import Lenis from 'lenis';
import { AuthProvider } from './context/AuthContext';
import OceanScene from './components/three/OceanScene';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import LoadingScreen from './components/ui/LoadingScreen';
import ProtectedRoute from './components/ProtectedRoute';

// Import pages
import Home from './pages/Home';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import Dashboard from './pages/Dashboard';

function AppContent() {
  const location = useLocation();
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register';
  const isDashboard = location.pathname === '/dashboard';

  useEffect(() => {
    // Only initialize smooth scroll for home page
    if (!isAuthPage && !isDashboard) {
      const lenis = new Lenis({
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
      });

      function raf(time) {
        lenis.raf(time);
        requestAnimationFrame(raf);
      }

      requestAnimationFrame(raf);

      return () => lenis.destroy();
    }
  }, [isAuthPage, isDashboard]);

  return (
    <div className="relative min-h-screen bg-ocean-dark overflow-x-hidden">
      {/* Fixed Three.js Ocean Background */}
      <div className="fixed inset-0 z-0">
        <Canvas
          camera={{ position: [0, 0, 5], fov: 75 }}
          dpr={[1, 1]}
          performance={{ min: 0.5, max: 0.8 }}
          frameloop="demand"
          gl={{
            antialias: false,
            powerPreference: 'high-performance',
            alpha: false,
            stencil: false,
            depth: true,
            logarithmicDepthBuffer: false,
          }}
        >
          <PerformanceMonitor onDecline={() => {}} factor={1} flipflops={3}>
            <Suspense fallback={null}>
              <OceanScene />
            </Suspense>
          </PerformanceMonitor>
        </Canvas>
      </div>

      {/* Main Content */}
      <div className="relative z-10">
        {!isDashboard && !isAuthPage && <Navbar />}

        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Suspense>

        {!isAuthPage && !isDashboard && <Footer />}
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;